import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, ArrowRight, MapPin, BatteryCharging, Zap, Leaf, Fuel, ShieldCheck, CheckCircle2, Camera, Activity, TimerReset } from 'lucide-react';
import { trackEvent, EVENTS } from '@/lib/analytics';

const STATS = [
  { icon: Zap, value: '100 kW', label: 'Installed Power' },
  { icon: BatteryCharging, value: '96 kWh', label: 'Battery Capacity' },
  { icon: Fuel, value: '0', label: 'Diesel Generators Added' },
  { icon: ShieldCheck, value: '100%', label: 'Critical Loads Covered' },
];

const CHALLENGE = [
  'Grid outages threatening stoppages on critical production lines',
  'A diesel generator meant fuel cost, servicing, noise and emissions',
  'Monthly consumption up 52% over five months as production ramped',
  'Maximum demand breaching the 112 kW contract limit in 3 of 5 months',
];

const SOLUTION = [
  '100 kW / 96 kWh Voltide BESS deployed for critical-load backup',
  'Automatic islanding on grid failure — no diesel generator required',
  'Remotely monitored, with outage events logged to the Voltide console',
  'Sized to extend to TOD arbitrage and demand shaving in a future phase',
];

const GALLERY = [
  { src: '/images/betul-hero.jpg', caption: 'Voltide 100 kW / 96 kWh BESS installed at the Betul plant' },
  { src: '/images/betul-cabinet.jpg', caption: 'Voltide BESS cabinet — branded side panel' },
];

export default function CaseStudyBetulPage() {
  useEffect(() => {
    trackEvent(EVENTS.VIEW_CASE_STUDY, { study: 'tfl-betul' });
  }, []);

  return (
    <div data-testid="case-study-betul-page">
      {/* Back Link */}
      <section className="pt-8">
        <div className="voltide-container">
          <Link to="/case-studies" className="inline-flex items-center gap-2 text-sm text-[#57534E] hover:text-[#0061DD] transition-colors" data-testid="back-to-case-studies">
            <ArrowLeft className="w-4 h-4" /> Back to Case Studies
          </Link>
        </div>
      </section>

      {/* Hero */}
      <section className="py-16 lg:py-24">
        <div className="voltide-container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-up">
              <div className="flex flex-wrap gap-2 mb-6">
                <Badge className="rounded-full bg-blue-50 text-[#0061DD] border-blue-100">Manufacturing</Badge>
                <Badge variant="secondary" className="rounded-full bg-stone-100 text-[#57534E]">
                  <MapPin className="w-3 h-3 mr-1" /> Betul, Madhya Pradesh
                </Badge>
              </div>
              <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-[#1A1A1A] leading-[1.1] mb-4">
                A battery instead of a diesel generator
              </h1>
              <p className="text-xl text-[#0061DD] font-semibold mb-6">
                Technocraft Fashions, Betul plant — 100 kW / 96 kWh
              </p>
              <p className="text-[#57534E] leading-relaxed mb-8">
                The plant needed reliable backup for critical operations through grid outages. Rather than adding a diesel generator, it deployed a Voltide BESS — keeping production running and removing diesel cost and upkeep from the plan.
              </p>
              <Link to="/contact" onClick={() => trackEvent(EVENTS.START_RFQ, { source: 'case_study_betul' })}>
                <Button className="rounded-full bg-[#0061DD] hover:bg-[#004BB3] text-white px-8 py-6 text-lg font-semibold gap-2" data-testid="betul-get-quote-btn">
                  Get Similar Results <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
            </div>
            <div className="animate-fade-up stagger-2">
              <div className="rounded-2xl overflow-hidden shadow-lg bg-[#F9F9F7] aspect-[4/5] max-w-md mx-auto lg:ml-auto">
                <img src="/images/betul-hero.jpg" alt="Voltide 100 kW / 96 kWh BESS installed at the Betul plant" className="w-full h-full object-contain" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-white" data-testid="betul-stats">
        <div className="voltide-container">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {STATS.map((stat, i) => (
              <div key={stat.label} className="bento-card p-6 text-center" data-testid={`betul-stat-${i}`}>
                <stat.icon className="w-8 h-8 text-[#0061DD] mx-auto mb-3" />
                <p className="mono text-2xl sm:text-3xl font-bold text-[#1A1A1A]">{stat.value}</p>
                <p className="text-sm text-[#57534E] mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenge & Solution */}
      <section className="py-16 lg:py-20" data-testid="betul-challenge-solution">
        <div className="voltide-container">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bento-card p-8">
              <h2 className="text-2xl font-bold text-[#1A1A1A] mb-4">The Challenge</h2>
              <ul className="space-y-3">
                {CHALLENGE.map(item => (
                  <li key={item} className="flex items-start gap-3 text-sm text-[#57534E]">
                    <div className="w-2 h-2 rounded-full bg-red-400 mt-1.5 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bento-card p-8 bg-emerald-50/50 border-emerald-100">
              <h2 className="text-2xl font-bold text-[#1A1A1A] mb-4">The Solution</h2>
              <ul className="space-y-3">
                {SOLUTION.map(item => (
                  <li key={item} className="flex items-start gap-3 text-sm text-[#1A1A1A]">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Live Monitoring */}
      <section className="py-16 lg:py-20 bg-white" data-testid="betul-monitoring">
        <div className="voltide-container">
          <div className="max-w-3xl mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] mb-3 flex items-center gap-2">
              <Activity className="w-6 h-6 text-[#0061DD]" /> Live Monitoring
            </h2>
            <p className="text-[#57534E] leading-relaxed">
              Every deployment reports to the Voltide console — state of charge, grid status, discharge history and outage events, in real time.
            </p>
            <p className="mono text-sm text-[#0061DD] mt-3">console.voltide.energy</p>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-lg border border-stone-200 bg-[#F9F9F7]">
            <img src="/images/betul-console.jpg" alt="Voltide console showing live BESS status for TFL Betul" className="w-full object-contain" data-testid="betul-console-image" />
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-16 lg:py-20" data-testid="betul-results">
        <div className="voltide-container">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] text-center mb-12">The Results</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: TimerReset, title: 'Minimum Downtime', desc: 'Automatic islanding keeps critical operations running through grid outages, with no production stoppage.' },
              { icon: Fuel, title: 'Diesel Savings', desc: 'Critical loads run off the battery during outages instead of a generator, cutting diesel cost and upkeep.' },
              { icon: Leaf, title: 'Zero-Emission Backup', desc: 'No diesel generator installed — outage backup delivered entirely from the battery.' },
            ].map((r, i) => (
              <div key={r.title} className="bento-card p-6 text-center" data-testid={`betul-result-${i}`}>
                <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mx-auto mb-4">
                  <r.icon className="w-6 h-6 text-[#0061DD]" />
                </div>
                <h3 className="text-lg font-bold text-[#1A1A1A] mb-2">{r.title}</h3>
                <p className="text-sm text-[#57534E]">{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-16 lg:py-20 bg-white" data-testid="betul-gallery">
        <div className="voltide-container">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] flex items-center gap-2 mb-8">
            <Camera className="w-6 h-6 text-[#0061DD]" /> Deployment Gallery
          </h2>
          <div className="grid sm:grid-cols-2 gap-6 max-w-3xl">
            {GALLERY.map((photo, i) => (
              <figure key={photo.src} data-testid={`betul-gallery-${i}`}>
                <div className="rounded-2xl overflow-hidden bg-[#F9F9F7] aspect-[4/5]">
                  <img src={photo.src} alt={photo.caption} className="w-full h-full object-contain" />
                </div>
                <figcaption className="text-sm text-[#57534E] mt-3">{photo.caption}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-24 bg-[#0061DD]">
        <div className="voltide-container text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Considering a diesel generator?
          </h2>
          <p className="text-lg text-blue-100 mb-8 max-w-2xl mx-auto">
            Send us five months of bills and a load list. We'll size a battery alternative and show you the numbers side by side.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button className="rounded-full bg-white text-[#0061DD] hover:bg-blue-50 px-8 py-6 text-lg font-semibold gap-2" data-testid="betul-cta-quote-btn">
                Request a Site Assessment <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
            <Link to="/calculator">
              <Button variant="outline" className="rounded-full border-white/40 text-white hover:bg-white/10 px-8 py-6 text-lg font-medium">
                Calculate Your Savings
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
